import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
  Box,
  CircularProgress,
  Button,
  Popper,
  Fade,
  Paper,
  ClickAwayListener,
  List,
  ListItemButton,
  ListItemText,
} from "@mui/material";
import _ from "lodash";
import ACTIVITY_LOG from "../../assets/icons/activity-log.png";
import ACTIVITY_STATE from "../../assets/icons/activity-state.png";
import {
  ArrowDropDown,
  CalendarTodayOutlined,
  Check,
  Close,
} from "@mui/icons-material";
import Timeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineDot from "@mui/lab/TimelineDot";
import {
  ACTIVITY_MODAL_LISTITEM,
  ACTIVITY_MODAL_SUBTITLE_TEXT,
  ACTIVITY_MODAL_TIMELINE_CONNECTOR,
  ACTIVITY_MODAL_TIMELINE_CONTENT_CAPTION,
  ACTIVITY_MODAL_TIMELINE_CONTENT_TEXT,
  ACTIVITY_MODAL_TIMELINE_CONTENT_TITLE,
  ACTIVITY_MODAL_TIMELINE_CONTENT_WRAPPER,
  ACTIVITY_MODAL_TIMELINE_DOT,
  ACTIVITY_MODAL_TIMELINE_DOT_LABEL,
  ACTIVITY_MODAL_TIMELINE_WRAPPER,
  ACTIVITY_MODAL_TITLE_TEXT,
  ACTIVITY_MODAL_TITLE_WRAPPER,
  ITEM_CARD_DIVIDER,
  UPLOAD_DATE_FILTER_PAPER,
  UPLOAD_FILTER_BUTTON,
  UPLOAD_LIST_ITEM,
} from "../styles";
import { getAuditLogs } from "../../services/products";
import {
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear,
} from "date-fns";

const RANGE_LIST = ["Last 7 Days", "This Month", "Last Month", "This Year"];

export const ActivityModal = (props: any) => {
  const { openActivityModal, onCloseActivityModal, recordID, upc } = props;

  const [isLoading, setIsLoading] = React.useState<boolean>(true);
  const [activities, setActivites] = React.useState<any>(null);
  const [selectedRange, setSelectedRange] = React.useState<number>(0);
  const [anchorElRange, setAnchorElRange] =
    React.useState<HTMLButtonElement | null>(null);
  const [openRangeFilter, setOpenRangeFilter] = React.useState(false);
  const [dateRange, setDateRange] = React.useState<any>({
    StartDate: format(Date.now() - 7 * 24 * 60 * 60 * 1000, "MM/dd/yyyy"),
    EndDate: format(new Date(), "MM/dd/yyyy"),
  });

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      const logs = await getAuditLogs({
        RecordId: recordID,
        ...dateRange,
      });
      const data = _.get(logs, "body", null) || null;
      if (logs?.statusCode === 200 && !_.isEmpty(data)) {
        setActivites(data);
      } else {
        setActivites(null);
      }
      setIsLoading(false);
    } catch (error) {
      console.error("[fetchLogs]: ", error);
      setIsLoading(false);
    }
  };

  React.useEffect(() => {
    if (openActivityModal) {
      fetchLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openActivityModal, dateRange]);

  const getFormattedDate = (date: string) => {
    if (!date) return;

    return format(new Date(date), "MMM d, yyyy") || "";
  };

  const getFormattedTime = (dateTime: string) => {
    if (!dateTime) return;

    return format(new Date(dateTime), "HH:mm") || "";
  };

  const handleRangeFilterClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorElRange(event.currentTarget);
    setOpenRangeFilter((prev) => !prev);
  };

  const handleRangeClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    index: number
  ) => {
    switch (index) {
      case 0:
        // Last 7 Days
        setDateRange({
          StartDate: format(Date.now() - 7 * 24 * 60 * 60 * 1000, "MM/dd/yyyy"),
          EndDate: format(new Date(), "MM/dd/yyyy"),
        });
        break;
      case 1:
        // This Month
        setDateRange({
          StartDate: format(startOfMonth(new Date()), "MM/dd/yyyy"),
          EndDate: format(endOfMonth(new Date()), "MM/dd/yyyy"),
        });
        break;
      case 2:
        // Last Month
        const date = new Date();
        setDateRange({
          StartDate: format(
            new Date(date.getFullYear(), date.getMonth() - 1, 1),
            "MM/dd/yyyy"
          ),
          EndDate: format(
            new Date(date.getFullYear(), date.getMonth(), 0),
            "MM/dd/yyyy"
          ),
        });
        break;
      case 3:
        // This Year
        setDateRange({
          StartDate: format(startOfYear(new Date()), "MM/dd/yyyy"),
          EndDate: format(endOfYear(new Date()), "MM/dd/yyyy"),
        });
        break;

      default:
        break;
    }
    setSelectedRange(index);
    setOpenRangeFilter(false);
  };

  const renderTimeline = (time: string, items: any, i: number) => {
    return (
      <Timeline
        key={`logItem_${time}_${i}`}
        sx={ACTIVITY_MODAL_TIMELINE_WRAPPER}
      >
        <TimelineItem>
          <TimelineSeparator>
            <TimelineDot sx={ACTIVITY_MODAL_TIMELINE_DOT}>
              <img
                src={ACTIVITY_STATE}
                height={32}
                width={32}
                alt="state-icon"
              />
            </TimelineDot>
            <Typography sx={ACTIVITY_MODAL_TIMELINE_DOT_LABEL}>
              {getFormattedTime(time)}
            </Typography>
            <TimelineConnector sx={ACTIVITY_MODAL_TIMELINE_CONNECTOR} />
          </TimelineSeparator>
          <TimelineContent sx={{ py: "12px", px: 2 }}>
            {items.map((item: any, itemIndex: number) => (
              <Stack
                sx={ACTIVITY_MODAL_TIMELINE_CONTENT_WRAPPER}
                spacing={1}
                key={`activity_${itemIndex}`}
              >
                <Typography sx={ACTIVITY_MODAL_TIMELINE_CONTENT_TITLE}>
                  {item?.Title || ""}
                </Typography>
                <Stack direction={"row"} alignItems={"center"} spacing={1.5}>
                  <Typography
                    component={"div"}
                    sx={ACTIVITY_MODAL_TIMELINE_CONTENT_CAPTION}
                  >
                    Modified on:{" "}
                    <Box display={"inline"} color={"#282C36"}>
                      {format(
                        new Date(item?.LastUpdatedDateTime),
                        "MMM d, yyyy HH:ss"
                      ) || ""}
                    </Box>
                  </Typography>
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    sx={ITEM_CARD_DIVIDER}
                  />
                  <Typography
                    component={"div"}
                    sx={ACTIVITY_MODAL_TIMELINE_CONTENT_CAPTION}
                  >
                    by{" "}
                    <Box display={"inline"} color={"#282C36"}>
                      {item?.LastUpdatedBy || ""}
                    </Box>
                  </Typography>
                  <Divider
                    orientation="vertical"
                    variant="middle"
                    sx={ITEM_CARD_DIVIDER}
                  />
                  <Typography
                    component={"div"}
                    sx={ACTIVITY_MODAL_TIMELINE_CONTENT_CAPTION}
                  >
                    File name:{" "}
                    <Box display={"inline"} color={"#282C36"}>
                      {item?.FileName || ""}
                    </Box>
                  </Typography>
                </Stack>
                <Stack spacing={1} marginTop={1}>
                  {item?.AfterValue && (
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                      padding={1}
                      sx={{ background: "rgba(229, 255, 248, 0.8)" }}
                    >
                      <Typography sx={ACTIVITY_MODAL_TIMELINE_CONTENT_TEXT}>
                        {item?.AfterValue || ""}
                      </Typography>
                      <Check sx={{ color: "#00805C", fontSize: 12 }} />
                    </Stack>
                  )}
                  {item?.BeforeValue && (
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      padding={1}
                      sx={{ background: "rgba(251, 233, 235, 0.74)" }}
                    >
                      <Typography sx={ACTIVITY_MODAL_TIMELINE_CONTENT_TEXT}>
                        {item?.BeforeValue || ""}
                      </Typography>
                    </Stack>
                  )}
                </Stack>
              </Stack>
            ))}
          </TimelineContent>
        </TimelineItem>
      </Timeline>
    );
  };
  return (
    <Dialog
      onClose={onCloseActivityModal}
      open={openActivityModal}
      PaperProps={{
        sx: {
          minWidth: 940,
        },
      }}
    >
      <DialogTitle sx={ACTIVITY_MODAL_TITLE_WRAPPER}>
        <Stack direction={"row"} alignItems={"center"} spacing={1.5}>
          <img
            src={ACTIVITY_LOG}
            height={32}
            width={32}
            alt="activity-log-icon"
          />
          <Typography sx={ACTIVITY_MODAL_TITLE_TEXT}>Activity Log</Typography>
        </Stack>
        <IconButton onClick={onCloseActivityModal}>
          <Close sx={{ fontSize: 18, color: "#6F7784" }} />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <Typography sx={ACTIVITY_MODAL_SUBTITLE_TEXT}>
            {`UPC Code: ${upc}`}
          </Typography>
          <Button
            variant="outlined"
            sx={UPLOAD_FILTER_BUTTON}
            startIcon={
              <CalendarTodayOutlined
                style={{ color: "#6F7784", fontSize: 14 }}
              />
            }
            endIcon={<ArrowDropDown sx={{ color: "#6F7784" }} />}
            onClick={handleRangeFilterClick}
          >
            {RANGE_LIST[selectedRange]}
          </Button>
        </Stack>
        <Divider sx={{ background: "rgba(15, 61, 97, 0.08)", marginTop: 2 }} />
        {isLoading ? (
          <Stack justifyContent={"center"} alignItems={"center"} height={250}>
            <CircularProgress sx={{ color: "#276B8B" }} />
            <Typography sx={{ color: "#276B8B" }} variant="body2">
              {"Loading logs..."}
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={4} mt={3} maxHeight={"60vh"} overflow={"auto"}>
            {activities ? (
              Object.keys(activities).map((key: string, index: number) => (
                <Stack spacing={3} key={`log_${key}_${index}`}>
                  <Typography sx={ACTIVITY_MODAL_SUBTITLE_TEXT}>
                    {getFormattedDate(key)}
                  </Typography>
                  {Object.keys(activities[key]).map(
                    (childKey: string, childIndex: number) =>
                      renderTimeline(
                        `${key} ${childKey}`,
                        activities[key][childKey],
                        childIndex
                      )
                  )}
                </Stack>
              ))
            ) : (
              <Stack
                justifyContent={"center"}
                alignItems={"center"}
                height={150}
              >
                <Typography sx={{ color: "#6F7784" }} variant="body2">
                  {"There are no log messages to display."}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </DialogContent>
      <Popper
        open={openRangeFilter}
        anchorEl={anchorElRange}
        placement={"bottom-end"}
        transition
        disablePortal
        role={undefined}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <Paper sx={UPLOAD_DATE_FILTER_PAPER} elevation={0}>
              <ClickAwayListener onClickAway={() => setOpenRangeFilter(false)}>
                <List dense>
                  <ListItemButton
                    dense
                    sx={ACTIVITY_MODAL_LISTITEM}
                    selected={selectedRange === 0}
                    onClick={(event) => handleRangeClick(event, 0)}
                  >
                    <ListItemText
                      sx={UPLOAD_LIST_ITEM}
                      disableTypography
                      primary={RANGE_LIST[0]}
                    />
                  </ListItemButton>
                  <ListItemButton
                    dense
                    sx={ACTIVITY_MODAL_LISTITEM}
                    selected={selectedRange === 1}
                    onClick={(event) => handleRangeClick(event, 1)}
                  >
                    <ListItemText
                      sx={UPLOAD_LIST_ITEM}
                      disableTypography
                      primary={RANGE_LIST[1]}
                    />
                  </ListItemButton>
                  <ListItemButton
                    dense
                    sx={ACTIVITY_MODAL_LISTITEM}
                    selected={selectedRange === 2}
                    onClick={(event) => handleRangeClick(event, 2)}
                  >
                    <ListItemText
                      sx={UPLOAD_LIST_ITEM}
                      disableTypography
                      primary={RANGE_LIST[2]}
                    />
                  </ListItemButton>
                  <ListItemButton
                    dense
                    sx={ACTIVITY_MODAL_LISTITEM}
                    selected={selectedRange === 3}
                    onClick={(event) => handleRangeClick(event, 3)}
                  >
                    <ListItemText
                      sx={UPLOAD_LIST_ITEM}
                      disableTypography
                      primary={RANGE_LIST[3]}
                    />
                  </ListItemButton>
                </List>
              </ClickAwayListener>
            </Paper>
          </Fade>
        )}
      </Popper>
    </Dialog>
  );
};
